import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { apiClient } from '../services/api';
import CustomSelect from './CustomSelect';

// Helper component to render either CustomSelect or default HTML select based on mode
const SelectField = ({ isManualMode, value, onChange, options, placeholder, className, error, searchable = false }) => {
  if (isManualMode) {
    // Use default HTML select for manual mode
    return (
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${error ? 'border-red-500' : ''} ${className || ''}`}
      >
        <option value="">{placeholder || 'Select an option'}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  } else {
    // Use CustomSelect for normal mode
    return (
      <CustomSelect
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        className={className}
        error={error}
        searchable={searchable}
      />
    );
  }
};

const InvoiceDataReview = ({ uploadId, isManualMode = false, onCreateInvoice, onCancel }) => {
  const [loading, setLoading] = useState(true);
  const [uploadData, setUploadData] = useState(null);
  const [fbrData, setFbrData] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [provinces, setProvinces] = useState([]);
  const [hsCodes, setHsCodes] = useState([]);
  const [uomCodes, setUomCodes] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  const loadUploadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/invoice/upload-data/${uploadId}`);
      setUploadData(response.data.upload);

      // Convert to FBR format
      const convertResponse = await apiClient.post(`/invoice/convert-to-fbr/${uploadId}`);
      setFbrData(convertResponse.data.fbr_invoice_data);
      setEditedData(convertResponse.data.fbr_invoice_data);

    } catch (error) {
      console.error('Failed to load upload data:', error);
      toast.error('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  }, [uploadId]);

  const loadReferenceData = useCallback(async () => {
    try {
      // Load provinces
      const provincesResponse = await apiClient.get('/reference/provinces');
      setProvinces(provincesResponse.data.data.map(p => ({ value: p.code, label: p.description })));

      // Load HS codes (first 100 for dropdown)
      const hsResponse = await apiClient.get('/reference/hs-codes?limit=100');
      setHsCodes(hsResponse.data.data.map(h => ({ value: h.code, label: `${h.code} - ${h.description}` })));

      // Load UOM codes
      const uomResponse = await apiClient.get('/reference/uom');
      setUomCodes(uomResponse.data.data.map(u => ({ value: u.code, label: `${u.code} - ${u.description}` })));

    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  }, []);

  useEffect(() => {
    loadUploadData();
    loadReferenceData();
  }, [loadReferenceData, loadUploadData]);

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    setEditedData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addItem = () => {
    setEditedData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          hsCode: '',
          productDescription: '',
          rate: 0,
          uoM: 'PCS',
          quantity: 1,
          totalValues: 0,
          valueSalesExcludingST: 0,
          fixedNotifiedValueOrRetailPrice: 0,
          salesTaxApplicable: 0,
          salesTaxWithheldAtSource: 0,
          extraTax: 0,
          furtherTax: 0,
          sroScheduleNo: '',
          fedPayable: 0,
          discount: 0,
          saleType: 1,
          sroItemSerialNo: ''
        }
      ]
    }));
  };

  const removeItem = (index) => {
    setEditedData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const validateData = () => {
    const errors = {};

    // Required field validation
    if (!editedData.sellerNTNCNIC) errors.sellerNTNCNIC = 'Seller NTN/CNIC is required';
    if (!editedData.sellerBusinessName) errors.sellerBusinessName = 'Seller business name is required';
    if (!editedData.sellerProvince) errors.sellerProvince = 'Seller province is required';
    if (!editedData.sellerAddress) errors.sellerAddress = 'Seller address is required';
    if (!editedData.buyerBusinessName) errors.buyerBusinessName = 'Buyer business name is required';
    if (!editedData.buyerProvince) errors.buyerProvince = 'Buyer province is required';
    if (!editedData.saleOriginationProvince) errors.saleOriginationProvince = 'Sale origination province is required';
    if (!editedData.destinationOfSupply) errors.destinationOfSupply = 'Destination of supply is required';

    // Items validation
    if (!editedData.items || editedData.items.length === 0) {
      errors.items = 'At least one item is required';
    } else {
      editedData.items.forEach((item, index) => {
        if (!item.hsCode) errors[`item_${index}_hsCode`] = 'HS Code is required';
        if (!item.productDescription) errors[`item_${index}_productDescription`] = 'Product description is required';
        if (!item.uoM) errors[`item_${index}_uoM`] = 'Unit of measure is required';
        if (item.quantity <= 0) errors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
        if (item.rate < 0) errors[`item_${index}_rate`] = 'Rate cannot be negative';
      });
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateInvoice = async () => {
    if (!validateData()) {
      toast.error('Please fix validation errors before proceeding');
      return;
    }

    try {
      // Prepare data in the exact format expected by the invoice API
      const invoicePayload = {
        invoiceType: editedData.invoiceType || 'SALE_INVOICE',
        invoiceDate: editedData.invoiceDate,
        sellerNTNCNIC: editedData.sellerNTNCNIC,
        sellerBusinessName: editedData.sellerBusinessName,
        sellerProvince: editedData.sellerProvince,
        sellerAddress: editedData.sellerAddress,
        buyerNTNCNIC: editedData.buyerNTNCNIC || '',
        buyerBusinessName: editedData.buyerBusinessName,
        buyerProvince: editedData.buyerProvince,
        buyerAddress: editedData.buyerAddress || '',
        buyerRegistrationType: editedData.buyerRegistrationType || 'NTN',
        invoiceRefNo: editedData.invoiceRefNo || '',
        scenarioId: editedData.scenarioId || 'SN001',
        destinationOfSupply: editedData.destinationOfSupply,
        saleOriginationProvince: editedData.saleOriginationProvince,
        remarks: editedData.remarks || 'Created from uploaded invoice',
        items: editedData.items || []
      };

      console.log('Creating invoice with payload:', invoicePayload);

      // Create invoice using the edited data (use special upload endpoint)
      const response = await apiClient.post('/invoice/from-upload', invoicePayload);

      console.log('Invoice creation response:', response.data);

      toast.success('Invoice created successfully from uploaded data!');
      onCreateInvoice(response.data.invoice);
    } catch (error) {
      console.error('Failed to create invoice:', error);

      // More detailed error handling
      if (error.response?.data?.message) {
        toast.error(`Failed to create invoice: ${error.response.data.message}`);
      } else if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        toast.error(`Validation errors: ${errorMessages}`);
      } else {
        toast.error('Failed to create invoice. Please check all required fields.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading invoice data...</span>
      </div>
    );
  }

  if (!uploadData || !fbrData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Failed to load invoice data</p>
        <button
          onClick={onCancel}
          className="mt-4 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Review & Edit Invoice Data</h1>
            <p className="text-gray-600 mt-1">
              Review the extracted data from <strong>{uploadData.original_filename}</strong> and make any necessary corrections
            </p>
          </div>
          {isManualMode && (
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              ✏️ Manual Mode - Default Dropdowns
            </div>
          )}
        </div>
        <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
          <span>Confidence: {Math.round((uploadData.parse_result?.confidence || 0) * 100)}%</span>
          <span>Format: {uploadData.parse_result?.format}</span>
          <span>Size: {(uploadData.file_size / 1024).toFixed(1)}KB</span>
          {isManualMode && <span className="text-blue-600 font-medium">Using default HTML dropdowns</span>}
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Seller Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Seller Information</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seller NTN/CNIC *
              </label>
              <input
                type="text"
                value={editedData.sellerNTNCNIC || ''}
                onChange={(e) => handleFieldChange('sellerNTNCNIC', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.sellerNTNCNIC ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter seller NTN/CNIC"
              />
              {validationErrors.sellerNTNCNIC && (
                <p className="text-red-600 text-xs mt-1">{validationErrors.sellerNTNCNIC}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name *
              </label>
              <input
                type="text"
                value={editedData.sellerBusinessName || ''}
                onChange={(e) => handleFieldChange('sellerBusinessName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.sellerBusinessName ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter business name"
              />
              {validationErrors.sellerBusinessName && (
                <p className="text-red-600 text-xs mt-1">{validationErrors.sellerBusinessName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Province *
              </label>
              <SelectField
                isManualMode={isManualMode}
                value={editedData.sellerProvince}
                onChange={(value) => handleFieldChange('sellerProvince', value)}
                options={provinces}
                placeholder="Select Province"
                className="w-full"
                error={validationErrors.sellerProvince}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address *
              </label>
              <textarea
                value={editedData.sellerAddress || ''}
                onChange={(e) => handleFieldChange('sellerAddress', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.sellerAddress ? 'border-red-300' : 'border-gray-300'
                }`}
                rows="3"
                placeholder="Enter seller address"
              />
              {validationErrors.sellerAddress && (
                <p className="text-red-600 text-xs mt-1">{validationErrors.sellerAddress}</p>
              )}
            </div>
          </div>

          {/* Buyer Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Buyer Information</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buyer NTN/CNIC
              </label>
              <input
                type="text"
                value={editedData.buyerNTNCNIC || ''}
                onChange={(e) => handleFieldChange('buyerNTNCNIC', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter buyer NTN/CNIC (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name *
              </label>
              <input
                type="text"
                value={editedData.buyerBusinessName || ''}
                onChange={(e) => handleFieldChange('buyerBusinessName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.buyerBusinessName ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter buyer business name"
              />
              {validationErrors.buyerBusinessName && (
                <p className="text-red-600 text-xs mt-1">{validationErrors.buyerBusinessName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Province *
              </label>
              <SelectField
                isManualMode={isManualMode}
                value={editedData.buyerProvince}
                onChange={(value) => handleFieldChange('buyerProvince', value)}
                options={provinces}
                placeholder="Select Province"
                className="w-full"
                error={validationErrors.buyerProvince}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                value={editedData.buyerAddress || ''}
                onChange={(e) => handleFieldChange('buyerAddress', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Enter buyer address"
              />
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Date *
            </label>
            <input
              type="date"
              value={editedData.invoiceDate || ''}
              onChange={(e) => handleFieldChange('invoiceDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Reference No
            </label>
            <input
              type="text"
              value={editedData.invoiceRefNo || ''}
              onChange={(e) => handleFieldChange('invoiceRefNo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter invoice reference number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sale Origination Province *
            </label>
            <SelectField
              isManualMode={isManualMode}
              value={editedData.saleOriginationProvince}
              onChange={(value) => handleFieldChange('saleOriginationProvince', value)}
              options={provinces}
              placeholder="Select Province"
              className="w-full"
              error={validationErrors.saleOriginationProvince}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Destination of Supply *
            </label>
            <SelectField
              isManualMode={isManualMode}
              value={editedData.destinationOfSupply}
              onChange={(value) => handleFieldChange('destinationOfSupply', value)}
              options={provinces}
              placeholder="Select Province"
              className="w-full"
              error={validationErrors.destinationOfSupply}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks
            </label>
            <input
              type="text"
              value={editedData.remarks || ''}
              onChange={(e) => handleFieldChange('remarks', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter any remarks"
            />
          </div>
        </div>

        {/* Items Section */}
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Items</h3>
            <button
              onClick={addItem}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Add Item
            </button>
          </div>

          {validationErrors.items && (
            <p className="text-red-600 text-sm mb-4">{validationErrors.items}</p>
          )}

          <div className="space-y-4">
            {editedData.items?.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-gray-900">Item {index + 1}</h4>
                  {editedData.items.length > 1 && (
                    <button
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      HS Code *
                    </label>
                    <SelectField
                      isManualMode={isManualMode}
                      value={item.hsCode}
                      onChange={(value) => handleItemChange(index, 'hsCode', value)}
                      options={hsCodes}
                      placeholder="Select HS Code"
                      className="w-full"
                      error={validationErrors[`item_${index}_hsCode`]}
                      searchable
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit of Measure *
                    </label>
                    <SelectField
                      isManualMode={isManualMode}
                      value={item.uoM}
                      onChange={(value) => handleItemChange(index, 'uoM', value)}
                      options={uomCodes}
                      placeholder="Select UOM"
                      className="w-full"
                      error={validationErrors[`item_${index}_uoM`]}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity *
                    </label>
                    <input
                      type="number"
                      value={item.quantity || ''}
                      onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        validationErrors[`item_${index}_quantity`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Description *
                    </label>
                    <input
                      type="text"
                      value={item.productDescription || ''}
                      onChange={(e) => handleItemChange(index, 'productDescription', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        validationErrors[`item_${index}_productDescription`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter product description"
                    />
                  </div>


                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax Rate (SN)
                    </label>
                    <input
                      type="text"
                      value={(item.ratePercentage !== undefined && item.ratePercentage !== null && item.ratePercentage !== '')
                        ? item.ratePercentage
                        : (item.salesTaxRate ? `${item.salesTaxRate}%` : '')}
                      disabled
                      className="w-full px-3 py-2 border rounded-md bg-gray-50 text-gray-700 border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit Price (Excl. Tax) *
                    </label>
                    <input
                      type="number"
                      value={item.unitPriceExcludingTax ?? item.rate ?? ''}
                      onChange={(e) => {
                        const unitPrice = parseFloat(e.target.value) || 0;
                        handleItemChange(index, 'unitPriceExcludingTax', unitPrice);
                        // Auto-calculate total values (base before tax)
                        const totalBase = unitPrice * (item.quantity || 1);
                        handleItemChange(index, 'totalValues', totalBase);
                        handleItemChange(index, 'valueSalesExcludingST', totalBase);
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        validationErrors[`item_${index}_rate`] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Values
                    </label>
                    <input
                      type="number"
                      value={item.totalValues || ''}
                      onChange={(e) => handleItemChange(index, 'totalValues', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sales Tax Applicable
                    </label>
                    <input
                      type="number"
                      value={item.salesTaxApplicable || ''}
                      onChange={(e) => handleItemChange(index, 'salesTaxApplicable', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateInvoice}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Create FBR Invoice
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDataReview;
